import classnames from 'classnames';
import React, { forwardRef } from 'react';
import { isEmpty } from 'validate.js';

import Icon from '../components/Icon';

const BASE_CLASSES = 'fw-medium br2 ba tc';
const DEFAULT_ICON_SIZE = '18px';

export const BUTTON_VARIANTS = {
  primary: classnames(BASE_CLASSES, 'bg-blue-500 b--blue-500 white'),
  secondary: classnames(BASE_CLASSES, 'bg-pale-blue b--pale-blue stone-700'),
  danger: classnames(BASE_CLASSES, 'bg-subtle-gray b--subtle-gray rubber'),
  rubber: classnames(BASE_CLASSES, 'bg-rubber b--rubber white'),
  tertiary: classnames(BASE_CLASSES, 'bg-subtle-gray b--subtle-gray stone-700'),
  outlineBlue: classnames(BASE_CLASSES, 'b--stone-700 stone-700'),
  outline: classnames(BASE_CLASSES, 'bg-white b--default stone-700'),
  ghost: classnames(BASE_CLASSES, 'b--transparent stone-700'),
  ghostGray: classnames(BASE_CLASSES, 'b--gray-300 secondary'),
  dark: classnames(BASE_CLASSES, 'b--black-50 bg-secondary white')
} as const;

export const BUTTON_SIZES = {
  default: 'ph12 pv3',
  small: 'ph8 pv2',
  xsmall: 'ph6 pv2',
  xxsmall: 'ph4 pv3',
  tiny: 'ph4 pv2 lg:font-14',
  icon: 'ph2 pv2'
} as const;

type ButtonVariant = keyof typeof BUTTON_VARIANTS;
type ButtonSize = keyof typeof BUTTON_SIZES;

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  iconName?: string | null;
  iconPosition?: 'center' | 'right' | 'left';
  iconSize?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
}

function getIconComponent(name: string, size: string) {
  return <Icon key={name} name={name} height={size} width={size} />;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children = null,
      className,
      disabled = false,
      iconName = null,
      iconPosition = 'left',
      iconSize = DEFAULT_ICON_SIZE,
      variant = 'primary',
      size = 'tiny',
      type = 'button',
      ...buttonProps
    },
    ref
  ) => {
    const hasIcon = !isEmpty(iconName);
    const finalChildren: React.ReactNode[] = [children];

    if (iconName) {
      const iconComponent = getIconComponent(iconName, iconSize);

      if (iconPosition === 'left') {
        finalChildren.unshift(iconComponent);
      } else {
        finalChildren.push(iconComponent);
      }
    }

    return (
      <button
        ref={ref}
        className={classnames(
          'DSButton',
          BUTTON_VARIANTS[variant],
          BUTTON_SIZES[size],
          disabled && 'o-50',
          hasIcon && 'flex justify-center items-center gap-8',
          className
        )}
        disabled={disabled}
        // eslint-disable-next-line react/button-has-type
        type={type}
        {...buttonProps}
      >
        {finalChildren}
      </button>
    );
  }
);

Button.displayName = 'DSButton';

export default Button;
